import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
export function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    // Obfuscated recipient
    const r = [106, 49, 57, 56, 49, 114, 111, 100, 114, 105, 103, 117, 101, 122, 64, 121, 97, 104, 111, 111, 46, 99, 111, 109];
    const recipient = r.map(c => String.fromCharCode(c)).join('');
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${recipient}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          business: formData.business,
          message: formData.message,
          _subject: `New consultation request from ${formData.name}`
        })
      });
      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          business: '',
          message: ''
        });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };
  return <section id="contact" className="py-24 bg-sum-green relative overflow-hidden" data-id="element-39">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10" data-id="element-40">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" data-id="element-41">
          <defs data-id="element-42">
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-43">
              <path d="M0 40L40 0H20L0 20M40 40V20L20 40" stroke="currentColor" strokeWidth="2" fill="none" data-id="element-44" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" data-id="element-45" />
        </svg>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-46">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-47">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-48">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-green-50 mb-10 max-w-2xl mx-auto" data-id="element-49">
            Let us help you get more customers. Contact me today for a free
            consultation and let's talk about how we can boost your sales.
          </p>

          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl max-w-2xl mx-auto text-left" data-id="element-50">
            <h3 className="text-2xl font-bold text-sum-charcoal mb-6 text-center" data-id="element-51">
              Get Your Free Consultation
            </h3>

            {status === 'success' ? <div className="text-center py-8" data-id="element-52">
                <CheckCircle2 className="mx-auto text-sum-green mb-4" size={48} data-id="element-53" />
                <h4 className="text-xl font-bold text-sum-charcoal mb-2" data-id="element-54">
                  Message Sent!
                </h4>
                <p className="text-gray-600 mb-6" data-id="element-55">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
                <button type="button" onClick={() => setStatus('idle')} className="text-sum-green font-semibold hover:underline" data-id="element-56">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-57">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-58">
                  <div data-id="element-59">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-60">
                      Your Name
                    </label>
                    <input type="text" id="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-sum-green focus:border-sum-green outline-none transition-colors" placeholder="John Doe" data-id="element-61" />
                  </div>
                  <div data-id="element-62">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-63">
                      Email Address
                    </label>
                    <input type="email" id="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-sum-green focus:border-sum-green outline-none transition-colors" placeholder="john@example.com" data-id="element-64" />
                  </div>
                </div>
                <div data-id="element-65">
                  <label htmlFor="business" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-66">
                    Business Name
                  </label>
                  <input type="text" id="business" value={formData.business} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-sum-green focus:border-sum-green outline-none transition-colors" placeholder="My Awesome Business" data-id="element-67" />
                </div>
                <div data-id="element-68">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-69">
                    How can we help?
                  </label>
                  <textarea id="message" rows={4} required value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-sum-green focus:border-sum-green outline-none transition-colors resize-none" placeholder="I need a new website and/or help with Facebook..." data-id="element-70"></textarea>
                </div>

                {status === 'error' && <p className="text-red-600 text-sm text-center" data-id="element-71">
                    Something went wrong. Please try again.
                  </p>}

                <button type="submit" disabled={status === 'sending'} className="w-full bg-sum-gold hover:bg-yellow-500 text-sum-charcoal font-bold text-lg py-4 rounded-lg shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center" data-id="element-72">
                  {status === 'sending' ? <>
                      <Loader2 className="animate-spin mr-2" size={20} data-id="element-73" />
                      Sending...
                    </> : 'Send Message'}
                </button>
              </form>}
          </div>
        </motion.div>
      </div>
    </section>;
}